import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth, useTimer, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  LayoutDashboard, 
  Users, 
  Briefcase, 
  Clock, 
  UserCircle,
  LogOut,
  Menu,
  X,
  Play,
  Pause,
  Square,
  Building2,
  Shield,
  Settings,
  TrendingUp,
  FileText,
  Receipt,
  Bell,
  BarChart3,
  CheckCircle2,
  Eye,
  Mail,
  Package,
  ClipboardCheck,
  Star,
  DollarSign,
  Calculator,
  Database,
  CalendarCheck,
  FileCheck,
  Brain,
  LayoutGrid,
  ChevronDown,
  Zap,
  PieChart,
  Wrench,
} from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import NotificationBell from "./NotificationBell";

// Import Billora logos
import billoraDark from "../assets/billora-dark.png";
import billoraLight from "../assets/billora-light.png";

// ============================
// GROUPED NAVIGATION STRUCTURE
// ============================

// Full menu registry - all possible items with their metadata
const FULL_MENU_REGISTRY = [
  { href: "/dashboard", name: "Dashboard", icon: LayoutDashboard, module: "core", group: "Operacion" },
  { href: "/time", name: "Horas", icon: Clock, module: "core", group: "Operacion" },
  { href: "/project-management", name: "Gestión", icon: LayoutGrid, module: "core", group: "Operacion" },
  { href: "/clients", name: "Clientes", icon: Users, module: "core", group: "Negocio" },
  { href: "/projects", name: "Proyectos", icon: Briefcase, module: "core", group: "Negocio" },
  { href: "/retainers", name: "Contratos", icon: FileText, module: "retainers", group: "Negocio" },
  { href: "/period-close", name: "Cierre de Periodo", icon: CalendarCheck, module: "billing", group: "Finanzas", countryOnly: "CL" },
  { href: "/documentos-tributarios", name: "DTEs", icon: FileCheck, module: "billing", group: "Finanzas", countryOnly: "CL" },
  { href: "/invoices", name: "Facturas", icon: Receipt, module: "billing", group: "Finanzas", countryExclude: "CL" },
  { href: "/billing", name: "Facturacion", icon: Receipt, module: "billing", group: "Finanzas", countryExclude: "CL" },
  { href: "/analytics", name: "Rentabilidad", icon: TrendingUp, module: "analytics", group: "Finanzas" },
  { href: "/reports", name: "Reportes", icon: Mail, module: "reports", group: "Finanzas" },
  { href: "/client-reports", name: "Reportes Cliente", icon: PieChart, module: "reports", group: "Finanzas" },
  { href: "/users", name: "Equipo", icon: UserCircle, module: "core", group: "Administracion" },
  { href: "/rates", name: "Tarifas", icon: DollarSign, module: "core", group: "Administracion" },
  { href: "/approvals", name: "Aprobaciones", icon: ClipboardCheck, module: "core", group: "Administracion" },
  { href: "/weekly-close", name: "Cierre Semanal", icon: CheckCircle2, module: "core", group: "Administracion" },
  { href: "/capacity", name: "Capacidad", icon: BarChart3, module: "capacity", group: "Administracion" },
  { href: "/firm-settings", name: "Configuracion", icon: Wrench, module: "core", group: "Administracion" },
  { href: "/accounting/settings", name: "Contabilidad", icon: Calculator, module: "core", group: "Administracion" },
  { href: "/accounting", name: "Contabilidad", icon: Calculator, module: "core", group: "Operacion" },
];

// Abogado: simplified flat nav (they don't need groups)
const abogadoNavigation = [
  { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard, module: "core" },
  { name: "Horas", href: "/time", icon: Clock, module: "core" },
  { name: "Gestión", href: "/project-management", icon: LayoutGrid, module: "core" },
  { name: "Clientes", href: "/clients", icon: Users, module: "core" },
  { name: "Proyectos", href: "/projects", icon: Briefcase, module: "core" },
];

// Contador
const contadorNavigationGeneric = [
  { name: "Contabilidad", href: "/accounting", icon: Calculator, module: "core" },
  { name: "Clientes", href: "/clients", icon: Users, module: "core" },
  { name: "Proyectos", href: "/projects", icon: Briefcase, module: "core" },
  { name: "Contratos", href: "/retainers", icon: FileText, module: "retainers" },
  { name: "Reportes Cliente", href: "/client-reports", icon: FileText, module: "reports", permissionKey: "client_reports" },
];

const contadorNavigationChile = [
  { name: "Contabilidad", href: "/accounting", icon: Calculator, module: "core" },
  { name: "Clientes", href: "/clients", icon: Users, module: "core" },
  { name: "Proyectos", href: "/projects", icon: Briefcase, module: "core" },
  { name: "Contratos", href: "/retainers", icon: FileText, module: "retainers" },
  { name: "Cierre de Periodo", href: "/period-close", icon: CalendarCheck, module: "billing" },
  { name: "DTEs", href: "/documentos-tributarios", icon: FileCheck, module: "billing" },
  { name: "Reportes Cliente", href: "/client-reports", icon: FileText, module: "reports", permissionKey: "client_reports" },
];

// Admin: grouped navigation
const adminNavigationGroups = [
  {
    label: "Operacion",
    items: [
      { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard, module: "core" },
      { name: "Horas", href: "/time", icon: Clock, module: "core" },
      { name: "Gestión", href: "/project-management", icon: LayoutGrid, module: "core" },
    ]
  },
  {
    label: "Negocio",
    items: [
      { name: "Clientes", href: "/clients", icon: Users, module: "core" },
      { name: "Proyectos", href: "/projects", icon: Briefcase, module: "core" },
      { name: "Contratos", href: "/retainers", icon: FileText, module: "retainers" },
    ]
  },
  {
    label: "Finanzas",
    items: [
      // Generic billing
      { name: "Facturas", href: "/invoices", icon: Receipt, module: "billing", countryExclude: "CL" },
      { name: "Facturacion", href: "/billing", icon: Receipt, module: "billing", countryExclude: "CL" },
      // Chile billing
      { name: "Cierre de Periodo", href: "/period-close", icon: CalendarCheck, module: "billing", countryOnly: "CL" },
      { name: "DTEs", href: "/documentos-tributarios", icon: FileCheck, module: "billing", countryOnly: "CL" },
      // Analytics
      { name: "Rentabilidad", href: "/analytics", icon: TrendingUp, module: "analytics" },
      { name: "Reportes", href: "/reports", icon: Mail, module: "reports" },
      { name: "Reportes Cliente", href: "/client-reports", icon: PieChart, module: "reports" },
    ]
  },
  {
    label: "Administracion",
    collapsed: true,
    items: [
      { name: "Equipo", href: "/users", icon: UserCircle, module: "core" },
      { name: "Tarifas", href: "/rates", icon: DollarSign, module: "core" },
      { name: "Aprobaciones", href: "/approvals", icon: ClipboardCheck, module: "core" },
      { name: "Cierre Semanal", href: "/weekly-close", icon: CheckCircle2, module: "core" },
      { name: "Capacidad", href: "/capacity", icon: BarChart3, module: "capacity" },
      { name: "Configuracion", href: "/firm-settings", icon: Wrench, module: "core" },
      { name: "Contabilidad", href: "/accounting/settings", icon: Calculator, module: "core" },
    ]
  },
];

// SuperAdmin
const superadminNavigation = [
  { name: "Panel de Control", href: "/superadmin", icon: LayoutDashboard },
  { name: "Vista Advisor", href: "/advisor", icon: Eye },
  { name: "Rentabilidad Clientes", href: "/advisor/profitability", icon: TrendingUp },
  { name: "Analisis IA", href: "/ai-analysis", icon: Brain },
  { name: "Gestion de Firmas", href: "/admin", icon: Building2 },
  { name: "Suscripciones", href: "/subscriptions", icon: Package },
  { name: "Early Adopter", href: "/early-adopter-admin", icon: Star },
  { name: "Backup BD", href: "/database-backup", icon: Database },
];

// Filter items based on role, modules, country
const filterItems = (items, enabledModules, countryCode, dteModuleEnabled) => {
  return items.filter(item => {
    if (item.module && !enabledModules.includes(item.module)) return false;
    if (item.countryOnly && item.countryOnly !== countryCode) return false;
    if (item.countryExclude && item.countryExclude === countryCode) return false;
    if (item.href === "/documentos-tributarios" && !dteModuleEnabled) return false;
    return true;
  });
};

const getNavigation = (role, enabledModules = ["core"], countryCode = "CL", dteModuleEnabled = true, contadorPermissions = {}, extraMenuRoutes = []) => {
  if (role === "superadmin") return { type: "flat", items: superadminNavigation };

  // Helper: get extra nav items not already in the base list
  const getExtraItems = (baseHrefs) => {
    if (!extraMenuRoutes || extraMenuRoutes.length === 0) return [];
    return extraMenuRoutes
      .filter(route => !baseHrefs.includes(route))
      .map(route => FULL_MENU_REGISTRY.find(r => r.href === route))
      .filter(Boolean);
  };

  if (role === "abogado") {
    const baseItems = filterItems(abogadoNavigation, enabledModules, countryCode, dteModuleEnabled);
    const baseHrefs = baseItems.map(i => i.href);
    const extraItems = getExtraItems(baseHrefs);
    
    if (extraItems.length === 0) {
      return { type: "flat", items: baseItems };
    }
    
    // Convert to grouped when extra items exist for better organization
    const groups = {};
    baseItems.forEach(item => {
      const reg = FULL_MENU_REGISTRY.find(r => r.href === item.href);
      const group = reg?.group || "Operacion";
      if (!groups[group]) groups[group] = [];
      groups[group].push(item);
    });
    extraItems.forEach(item => {
      const group = item.group || "Extra";
      if (!groups[group]) groups[group] = [];
      groups[group].push(item);
    });
    const groupOrder = ["Operacion", "Negocio", "Finanzas", "Administracion", "Extra"];
    const sortedGroups = groupOrder
      .filter(g => groups[g]?.length > 0)
      .map(g => ({ label: g, items: groups[g], collapsed: g === "Administracion" }));
    
    return sortedGroups.length > 1 ? { type: "grouped", groups: sortedGroups } : { type: "flat", items: [...baseItems, ...extraItems] };
  }

  if (role === "contador") {
    const nav = countryCode === "CL" ? contadorNavigationChile : contadorNavigationGeneric;
    const baseItems = filterItems(nav, enabledModules, countryCode, dteModuleEnabled).filter(item => {
      if (item.permissionKey) {
        return contadorPermissions[item.permissionKey]?.can_access === true;
      }
      return true;
    });
    const baseHrefs = baseItems.map(i => i.href);
    const extraItems = getExtraItems(baseHrefs);
    
    if (extraItems.length === 0) {
      return { type: "flat", items: baseItems };
    }
    
    const groups = {};
    baseItems.forEach(item => {
      const reg = FULL_MENU_REGISTRY.find(r => r.href === item.href);
      const group = reg?.group || "Operacion";
      if (!groups[group]) groups[group] = [];
      groups[group].push(item);
    });
    extraItems.forEach(item => {
      const group = item.group || "Extra";
      if (!groups[group]) groups[group] = [];
      groups[group].push(item);
    });
    const groupOrder = ["Operacion", "Negocio", "Finanzas", "Administracion", "Extra"];
    const sortedGroups = groupOrder
      .filter(g => groups[g]?.length > 0)
      .map(g => ({ label: g, items: groups[g], collapsed: g === "Administracion" }));
    
    return sortedGroups.length > 1 ? { type: "grouped", groups: sortedGroups } : { type: "flat", items: [...baseItems, ...extraItems] };
  }

  // Admin: grouped - add extra items to their respective groups
  const groups = adminNavigationGroups.map(g => ({
    ...g,
    items: filterItems(g.items, enabledModules, countryCode, dteModuleEnabled),
  })).filter(g => g.items.length > 0);

  // Extra items for admin (rare but possible)
  if (extraMenuRoutes && extraMenuRoutes.length > 0) {
    const adminHrefs = groups.flatMap(g => g.items.map(i => i.href));
    const extraItems = getExtraItems(adminHrefs);
    extraItems.forEach(item => {
      const targetGroup = groups.find(g => g.label === item.group);
      if (targetGroup) {
        targetGroup.items.push(item);
      } else {
        groups.push({ label: item.group || "Extra", items: [item] });
      }
    });
  }

  return { type: "grouped", groups };
};

const formatTime = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

const Sidebar = ({ mobile = false, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsedGroups, setCollapsedGroups] = useState({ Administracion: true });
  const [contadorPermissions, setContadorPermissions] = useState({});

  useEffect(() => {
    if (user?.role !== "contador") return;
    axios.get(`${API}/accounting/my-permissions`).then(res => {
      setContadorPermissions(res.data || {});
    }).catch(() => {});
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate("/login");
    if (onClose) onClose();
  };

  const enabledModules = user?.enabled_modules || ["core"];
  const countryCode = user?.country_code || "CL";
  const dteModuleEnabled = user?.dte_module_enabled !== false;
  const extraMenuRoutes = user?.extra_menu_routes || [];

  const navData = getNavigation(user?.role, enabledModules, countryCode, dteModuleEnabled, contadorPermissions, extraMenuRoutes);

  const NavItem = ({ item }) => (
    <NavLink
      to={item.href}
      onClick={onClose}
      className={({ isActive }) =>
        `sidebar-item ${isActive ? "sidebar-item-active" : ""}`
      }
    >
      <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
      <span className="font-medium">{item.name}</span>
    </NavLink>
  );

  const toggleGroup = (label) => {
    setCollapsedGroups(prev => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <div className="flex flex-col h-[100dvh] bg-slate-900 text-white overflow-hidden">
      {/* Logo */}
      <div className="p-5 border-b border-slate-800 shrink-0">
        <img src={billoraLight} alt="Billora" className="h-7" />
        <p className="text-xs text-slate-400 mt-1.5">Gestion de Tiempo Legal</p>
      </div>

      {/* Navigation */}
      <div className="flex-1 min-h-0 relative">
        <nav className="h-full p-3 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent hover:scrollbar-thumb-slate-600">
          {navData.type === "flat" ? (
            <div className="space-y-0.5">
              {navData.items.map((item) => (
                <NavItem key={item.href} item={item} />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {navData.groups.map((group) => {
                const isCollapsed = collapsedGroups[group.label];
                return (
                  <div key={group.label}>
                    <button
                      onClick={() => toggleGroup(group.label)}
                      className="flex items-center justify-between w-full px-2 mb-1 group"
                    >
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 group-hover:text-slate-400 transition-colors">
                        {group.label}
                      </span>
                      <ChevronDown className={`w-3 h-3 text-slate-600 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                    </button>
                    {!isCollapsed && (
                      <div className="space-y-0.5">
                        {group.items.map((item) => (
                          <NavItem key={item.href} item={item} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-slate-900 to-transparent pointer-events-none opacity-60" />
      </div>

      {/* User info & logout */}
      <div className="p-3 border-t border-slate-800 shrink-0">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center shrink-0">
            <span className="text-xs font-semibold">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
          </div>
        </div>
        <NavLink
          to="/profile"
          onClick={onClose}
          className={({ isActive }) =>
            `sidebar-item mb-1 ${isActive ? "sidebar-item-active" : ""}`
          }
          data-testid="profile-link"
        >
          <Settings className="w-4 h-4" strokeWidth={1.5} />
          <span className="font-medium text-sm">Mi Perfil</span>
        </NavLink>
        <Button
          variant="ghost"
          className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800 h-9"
          onClick={handleLogout}
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
          <span className="text-sm">Cerrar Sesion</span>
        </Button>
      </div>
    </div>
  );
};

const FloatingTimer = () => {
  const { isRunning, isPaused, seconds, timerProject, timerDescription, pauseTimer, resumeTimer, stopTimer, notifyEntrySaved } = useTimer();
  const navigate = useNavigate();

  if (!isRunning) return null;

  const roundToFiveMinutes = (minutes) => {
    return Math.round(minutes / 5) * 5;
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins} minutos`;
    if (mins === 0) return `${hours} hora${hours > 1 ? 's' : ''}`;
    return `${hours}h ${mins}m`;
  };

  const handleStop = async () => {
    const projectData = timerProject ? { ...timerProject } : null;
    const descriptionData = timerDescription || "Trabajo sin descripcion";
    const currentSeconds = seconds;
    
    stopTimer();
    
    const rawMinutes = Math.ceil(currentSeconds / 60);
    const totalMinutes = Math.max(5, roundToFiveMinutes(rawMinutes));
    
    if (currentSeconds < 60) {
      toast.info("Tiempo menor a 1 minuto, no registrado");
      navigate("/time");
      return;
    }
    
    if (!projectData || !projectData.id) {
      toast.error("Error: No se encontro informacion del proyecto");
      navigate("/time");
      return;
    }
    
    try {
      const payload = {
        project_id: projectData.id,
        description: descriptionData,
        duration_minutes: totalMinutes,
        date: new Date().toISOString().split("T")[0],
        entry_type: "timer",
        currency: projectData.currency,
        hourly_rate: projectData.hourly_rate,
        is_billable: true,
      };
      
      await axios.post(`${API}/time-entries`, payload);
      toast.success(`Registrado: ${formatDuration(totalMinutes)}`);
      notifyEntrySaved();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Error desconocido";
      toast.error(`Error al guardar: ${errorMsg}`);
    }
    
    navigate("/time");
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-slate-900 text-white rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-4" data-testid="floating-timer">
      <div>
        <p className="text-xs text-slate-400 truncate max-w-[200px]">{timerProject?.name || "Sin proyecto"}</p>
        <p className="text-xl font-mono font-semibold tracking-wider" data-testid="timer-display">
          {formatTime(seconds)}
        </p>
      </div>
      <div className="flex items-center gap-1">
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-slate-300 hover:text-white hover:bg-slate-800"
          onClick={isPaused ? resumeTimer : pauseTimer}
          data-testid="timer-pause-button"
        >
          {isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-slate-800"
          onClick={handleStop}
          data-testid="timer-stop-button"
        >
          <Square className="h-4 w-4" fill="currentColor" />
        </Button>
      </div>
    </div>
  );
};

const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#F8F7F5] overflow-x-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 z-30">
        <Sidebar />
      </aside>

      {/* Desktop Top Bar with Notification Bell */}
      <div className="hidden lg:flex lg:pl-64 fixed top-0 right-0 left-0 h-14 bg-[#F8F7F5]/80 backdrop-blur-md border-b border-[#EBE8E4]/50 z-20">
        <div className="flex-1 flex items-center justify-end px-6">
          <NotificationBell />
        </div>
      </div>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-40 bg-[#F8F7F5]/80 backdrop-blur-md border-b border-[#EBE8E4]/50">
        <div className="flex items-center justify-between px-4 py-3">
          <img src={billoraDark} alt="Billora" className="h-7" />
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64">
                <Sidebar mobile onClose={() => setMobileMenuOpen(false)} />
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:pl-64 lg:pt-14 overflow-x-hidden">
        <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-7xl mx-auto overflow-x-hidden">
          <Outlet />
        </div>
      </main>

      {/* Floating Timer */}
      <FloatingTimer />
    </div>
  );
};

export default Layout;
